import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearch } from 'src/Utils/search/search.hooks'
import { useGetURLParams } from 'src/pages/GRCDashboards/GrcDashboards.hooks'
import { useSearchContext } from 'src/Utils/search/search.context'
import {
  CloseIcon,
  SearchBar,
  SearchContainer,
  SearchIcon,
  SearchIconButton,
  SearchIconText,
  SearchInput,
  SearchInputContainer,
  SearchOption,
  SearchOptionsContainer,
} from './Search.styles'
import { AdvancedSearch } from './AdvancedSearch/AdvancedSearch'
import { ContentSearch } from './ContentSearch/ContentSearch'

export const Search = ({
  isExpanded,
  setIsExpanded,
  withContentSearch,
  paramPrefix,
}: {
  isExpanded: boolean
  setIsExpanded: (isExpanded: boolean) => void
  withContentSearch?: boolean
  paramPrefix?: string
}): JSX.Element => {
  const searchBarRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const search = useSearch(paramPrefix)
  const { setContentMatchIds } = useSearchContext()
  const searchParams = useGetURLParams()
  const searchParamValue = useMemo(
    () =>
      searchParams[paramPrefix ? `${paramPrefix}Search` : 'search'] as string,
    [searchParams, paramPrefix],
  )
  const isContentSearchActive = useMemo(() => {
    return (
      searchParams[
        paramPrefix ? `${paramPrefix}ContentSearch` : 'contentSearch'
      ] === 'true'
    )
  }, [searchParams, paramPrefix])

  useEffect(() => {
    if (searchParamValue) {
      setQuery(decodeURIComponent(searchParamValue))
      setIsExpanded(true)
    } else {
      setQuery('')
      setIsExpanded(false)
      setContentMatchIds(new Set())
    }
  }, [searchParamValue, setIsExpanded, setContentMatchIds])

  const handleEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      search(query, undefined, isContentSearchActive)
    }
  }

  const onClear = useCallback(() => {
    setQuery('')
    search('', undefined, isContentSearchActive)
    setContentMatchIds(new Set())
    searchInputRef.current?.focus()
  }, [search, isContentSearchActive, setContentMatchIds])

  return (
    <SearchContainer $isExpanded={isExpanded}>
      <SearchBar ref={searchBarRef}>
        <SearchInputContainer>
          <SearchIconButton
            onClick={() => {
              if (isExpanded) {
                onClear()
              } else {
                searchInputRef.current?.focus()
              }
              setIsExpanded(!isExpanded)
            }}
          >
            <SearchIcon />
            <SearchIconText>Search</SearchIconText>
          </SearchIconButton>
          <SearchInput
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleEnter}
            ref={searchInputRef}
          />
          {query.length > 0 && <CloseIcon onClick={onClear} />}
        </SearchInputContainer>
        <SearchOptionsContainer>
          {withContentSearch && (
            <SearchOption>
              <ContentSearch query={query} />
            </SearchOption>
          )}
          <SearchOption>
            <AdvancedSearch
              anchorRef={searchBarRef}
              query={query}
              onSearch={search}
            />
          </SearchOption>
        </SearchOptionsContainer>
      </SearchBar>
    </SearchContainer>
  )
}
